<template>
  <div id="wrapper">
    <div v-if="gpsDisabled" id="disabled-gps-message">
      {{ $t("gps.disabled1") }}<br/>
      {{ $t("gps.disabled2") }}<br/><br/>

      <button class="button is-success" @click="startGps">
        {{ $t("gps.disabledLetsGo") }}
      </button>
    </div>
    <div
      id="instructions"
      class="notification is-info"
      v-if="gpsDisabled === false"
      :class="{'disable-pointer-events': !huntingCoordinates}"
    >
      <p v-if="huntingResultNothing">
        {{ $t("hunting.areYouSure") }}<br/>
        {{ $t("hunting.iDontSee") }}<br/><br/>
        <button class="button is-default" @click="goToDashboard">
          {{ $t("common.abandon") }}
        </button>
        <button class="button is-primary" @click="modifyHuntCoordinates">
          {{ $t("common.retry") }}
        </button>
      </p>
      <p v-if="huntingResultAlready">
        {{ $t("hunting.alreadyFound") }}<br/><br/>
        <button class="button is-default" @click="goToDashboard">
          {{ $t("common.abandon") }}
        </button>
        <button class="button is-primary" @click="modifyHuntCoordinates">
          {{ $t("common.retry") }}
        </button>
      </p>
      <p v-if="huntingResultAtMax">
        {{ $t("hunting.yesButEnough") }}<br/><br/>
        <button class="button is-default" @click="goToDashboard">
          {{ $t("common.abandon") }}
        </button>
        <button class="button is-primary" @click="modifyHuntCoordinates">
          {{ $t("common.retry") }}
        </button>
      </p>
      <p v-if="huntingResultFound">
        {{ $t("hunting.congrats") }}<br/><br/>
        {{ $t("hunting.newElements") }}
        <br/>
        <button class="button is-default" @click="goToDashboard">
          {{ $t("common.goBack") }}
        </button>
      </p>
      <p v-if="!huntingResult && !huntingCoordinates">
        {{ $t("hunting.clickOnMap") }}
      </p>
      <p v-if="!huntingResult && huntingCoordinates">
        <button class="button is-default" @click="modifyHuntCoordinates">
          {{ $t("common.modify") }}
        </button>
        <button class="button is-primary" @click="checkHuntCoordinates">
          {{ $t("common.validate") }}
        </button>
      </p>
    </div>
    <div id="waiting-for-gps-signal" v-if="gpsDisabled === undefined && !position">
      <img src="/img/gps-signal.png"/>
      <progress class="progress is-small is-default" max="100">15%</progress>
    </div>
    <div id="map" v-if="gpsDisabled === false">
      <mgl-map
          ref="map"
          :zoom="19"
          :bearing="0"
          :dragPan="false"
          :dragRotate="false"
          :pitchWithRotate="false"
          :touchZoomRotate="false"
          :scrollZoom="false"
          :fadeDuration="0"
          :pitch="0"
          @map:click="onClick"
          @map:load="onLoadMap"
          language="fr"
      >
      </mgl-map>
    </div>
  </div>
</template>

<script>
import 'maplibre-gl/dist/maplibre-gl.css'
import GpsSceneMixin from '@/mixins/GpsScene'
import PuzzleMixin from '@/mixins/Puzzle'
import * as turf from '@turf/circle'
import { MglMap, MglDefaults } from 'vue-maplibre-gl'

MglDefaults.style = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 512,
      maxzoom: 19
    }
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm'
    }
  ]
}

export default {
  name: 'HuntingMap',
  mixins: [GpsSceneMixin, PuzzleMixin],
  props: ['huntingKey'],
  components: {
    MglMap
  },
  data () {
    return {
      watchUpdates: false,
      huntingCoordinates: null,
      huntingResult: null
    }
  },
  computed: {
    currentPositionGeoJson () {
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: this.centerCoordinates
            }
          }
        ]
      }
    },
    centerCoordinates () {
      if (!this.position) {
        return []
      }
      // Center map on current position
      return [this.position.coords.longitude, this.position.coords.latitude]
    },
    huntingResultNothing () {
      // Nothing found
      if (!this.huntingResult) {
        return false
      }
      return this.huntingResult.nb_new_items_found + this.huntingResult.nb_items_already_found + this.huntingResult.nb_items_at_max === 0
    },
    huntingResultFound () {
      // YaY ! New location found !
      if (!this.huntingResult) {
        return false
      }
      return this.huntingResult.nb_new_items_found > 0
    },
    huntingResultAlready () {
      // Oh. You already found this one
      if (!this.huntingResult) {
        return false
      }
      return this.huntingResult.nb_new_items_found === 0 && this.huntingResult.nb_items_already_found > 0
    },
    huntingResultAtMax () {
      // Oh. Enough already with this one !
      if (!this.huntingResult) {
        return false
      }
      return this.huntingResult.nb_new_items_found === 0 && this.huntingResult.nb_items_already_found === 0 && this.huntingResult.nb_items_at_max > 0
    }
  },
  mounted () {
    this.huntingResult = null
  },
  methods: {
    async onLoadMap (el) {
      this.map = el.map
      this.map.setCenter(this.centerCoordinates)
      const image = await el.map.loadImage('/img/gps-current-position.webp')
      el.map.addImage('person', image.data)

      // Add current position
      el.map.addSource('point', {
        type: 'geojson',
        data: this.currentPositionGeoJson
      })
      el.map.addLayer({
        id: 'points',
        type: 'symbol',
        source: 'point',
        layout: {
          'icon-image': 'person',
          'icon-size': 0.15,
          'icon-allow-overlap': true
        }
      })

      // Add the circle as a GeoJSON source
      el.map.addSource('location-radius', {
        type: 'geojson',
        data: this.currentPositionGeoJson
      })

      // Add a fill layer with some transparency
      el.map.addLayer({
        id: 'location-radius',
        type: 'fill',
        source: 'location-radius',
        paint: {
          'fill-color': '#FF0000',
          'fill-opacity': 0
        }
      })
    },
    goToDashboard () {
      this.$emit('go-to-dashboard', { go: true })
    },
    onClick (evt) {
      if (!evt.event.lngLat || this.huntingCoordinates) {
        return
      }
      this.huntingCoordinates = [evt.event.lngLat.lng, evt.event.lngLat.lat]

      if (this.huntingCoordinates) {
        const circle = turf.circle(this.huntingCoordinates, 0.003)

        const source = this.map.getSource('location-radius')
        source.setData(circle)

        this.map.setPaintProperty('location-radius', 'fill-opacity', 0.5)
      }
    },
    onDistanceUpdate (distance) {
    },
    modifyHuntCoordinates () {
      this.huntingCoordinates = null
      this.huntingResult = null
      this.map.setPaintProperty('location-radius', 'fill-opacity', 0)
    },
    async checkHuntCoordinates () {
      this.huntingResult = await this.$store.dispatch('checkHuntCoordinates',
        {
          huntingKey: this.huntingKey,
          value: [this.huntingCoordinates[1], this.huntingCoordinates[0]]
        })
      if (this.huntingResult.nb_new_items_found > 0) {
        this.playVictorySound()
      } else {
        this.playFailedSound()
      }
    }
  }
}
</script>

<style scoped lang="sass">
  #wrapper
    height: 100%

  #map
      width: 100%
      height: 100%
      background-color: white

  .wait-for-gps
    padding-top: 20px

  .progress
    width: 200px
    margin: auto

#instructions, #waiting-for-gps-signal
  position: absolute
  z-index: 2000
  width: 100%
  text-align: center
  ul
    text-align: left

#waiting-for-gps-signal
  margin-top: 40px
  img
    display: block
    width: 60px
    margin: auto
  .progress
    margin: auto
    margin-top: 5px
    width: 200px

#disabled-gps-message
  padding-top: 100px

#instructions
  bottom: 0px
  margin-bottom: 10px
  text-align: center
  padding: 10px 50px
  box-sizing: border-box
  color: black
  background-color: rgba(255, 255, 255, 0.5)
  p
    margin: 0px
    font-weight: bold
    span
      font-weight: normal
      display: block
      padding-top: 5px
  button
    margin: 10px 0px 0px 0px
  button:nth-of-type(1)
    margin-right: 20px

.disable-pointer-events
  pointer-events: none

</style>
